import React, { useState } from 'react';
import { useGetAdvertisingReportQuery, useGetCountriesQuery, useGetCitiesQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import PieChart from '../../../components/charts/PieChart';
import BarChart from '../../../components/charts/BarChart';
import { useLanguage } from '../../../context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp,
  faArrowTrendDown,
  faBullhorn,
  faExchangeAlt,
  faRotate,
  faUserTie,
  faSackDollar,
  faBullseye,
  faChartPie,
  faGlobe,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';

const PERIOD_OPTIONS = [
  { value: 'last_7_days',  label: 'Last 7 Days'  },
  { value: 'last_30_days', label: 'Last 30 Days'  },
  { value: 'last_90_days', label: 'Last 90 Days'  },
  { value: 'this_year',    label: 'This Year'     },
];

const PACKAGE_COLORS = [
  'rgba(57,106,84,0.85)',
  'rgba(248,168,36,0.85)',
  'rgba(100,116,139,0.70)',
  'rgba(59,130,246,0.75)',
  'rgba(168,85,247,0.75)',
];

const CATEGORY_COLORS = [
  'rgba(57,106,84,0.85)',
  'rgba(239,68,68,0.75)',
  'rgba(248,168,36,0.85)',
  'rgba(59,130,246,0.75)',
  'rgba(168,85,247,0.75)',
  'rgba(57,106,84,0.50)',
  'rgba(100,116,139,0.70)',
  'rgba(33,37,41,0.60)',
];

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${parseFloat((n / 1_000_000).toFixed(1))}M`;
  if (n >= 1_000)     return `${parseFloat((n / 1_000).toFixed(1))}k`;
  return n.toString();
}

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${parseFloat((n / 1_000_000).toFixed(1))}M`;
  if (n >= 1_000)     return `$${parseFloat((n / 1_000).toFixed(1))}k`;
  return `$${n.toFixed(0)}`;
}

interface StatCardProps {
  label: string;
  value: string;
  change: number | null;
  icon: any;
  iconColor?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, change, icon, iconColor = '#396A54' }) => (
  <div className="col-6 col-xl-4 col-xxl-2">
    <div className="card h-100">
      <div className="card-body p-6">
        <div className="d-flex align-items-start justify-content-between mb-4">
          <div className="d-flex flex-column gap-1">
            {change !== null ? (
              <span
                className="badge fw-semibold fs-8 d-inline-flex align-items-center gap-1"
                style={{
                  background: change >= 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                  color: change >= 0 ? '#16a34a' : '#dc2626',
                  width: 'fit-content',
                }}
              >
                <FontAwesomeIcon icon={change >= 0 ? faArrowTrendUp : faArrowTrendDown} style={{ fontSize: 10 }} />
                {change >= 0 ? '+' : ''}{change}%
              </span>
            ) : (
              <span style={{ height: 22, display: 'block' }} />
            )}
          </div>
          <div
            className="d-flex align-items-center justify-content-center rounded"
            style={{ width: 40, height: 40, background: `${iconColor}18`, flexShrink: 0 }}
          >
            <FontAwesomeIcon icon={icon} style={{ color: iconColor, fontSize: 18 }} />
          </div>
        </div>
        <div className="fw-bold text-dark mb-1" style={{ fontSize: 28, lineHeight: 1 }}>{value}</div>
        <div className="text-muted fw-semibold fs-7">{label}</div>
      </div>
    </div>
  </div>
);

const PACKAGE_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  free:       { bg: 'rgba(100,116,139,0.12)', color: '#475569' },
  basic:      { bg: 'rgba(59,130,246,0.12)',  color: '#2563eb' },
  premium:    { bg: 'rgba(168,85,247,0.12)',  color: '#7c3aed' },
  strategic:  { bg: 'rgba(248,168,36,0.15)',  color: '#b45309' },
  distinct:   { bg: 'rgba(57,106,84,0.12)',   color: '#396A54' },
};

const PackageBadge: React.FC<{ name: string }> = ({ name }) => {
  const key = name.toLowerCase().replace(/\s+/g, '');
  const style = Object.entries(PACKAGE_BADGE_COLORS).find(([k]) => key.includes(k))?.[1]
    ?? { bg: 'rgba(57,106,84,0.12)', color: '#396A54' };
  return (
    <span
      className="badge fw-semibold fs-8 text-capitalize"
      style={{ background: style.bg, color: style.color }}
    >
      {name}
    </span>
  );
};

const AdminAdvertisingRevenueReport: React.FC = () => {
  const { t } = useLanguage();
  const [countryId, setCountryId] = useState('');
  const [cityId,    setCityId]    = useState('');
  const [period,    setPeriod]    = useState('last_30_days');

  const params: Record<string, any> = { period };
  if (countryId) params.country_id = countryId;
  if (cityId)    params.city_id    = cityId;

  const { data, isLoading } = useGetAdvertisingReportQuery(params);
  const { data: countriesData } = useGetCountriesQuery({ per_page: 200 });
  const { data: citiesData }    = useGetCitiesQuery(countryId ? { country_id: countryId, per_page: 500 } : { per_page: 500 });

  const d = data?.data || {};

  const countries: any[] = countriesData?.data?.data || countriesData?.data || [];
  const cities: any[]    = citiesData?.data?.data    || citiesData?.data    || [];

  const revByPackage:  Array<{ label: string; total: number }>                                                                          = d.revenue_by_package  || [];
  const revByCategory: Array<{ label: string; total: number }>                                                                         = d.revenue_by_category || [];
  const localVsForeign: Array<{ country: string; local_pct: number; foreign_pct: number; local_count: number; foreign_count: number }> = d.local_vs_foreign    || [];
  const topSpenders:   Array<{ name: string; country: string; total_spending: number; package: string }>                               = d.top_spenders        || [];

  const statCards: StatCardProps[] = [
    {
      label: t('Active campaigns'),
      value: formatNumber(d.active_campaigns ?? 0),
      change: d.active_campaigns_change ?? null,
      icon: faBullhorn,
      iconColor: '#396A54',
    },
    {
      label: t('Switching from free to paid'),
      value: `${d.free_to_paid_rate ?? 0}%`,
      change: d.free_to_paid_change ?? null,
      icon: faExchangeAlt,
      iconColor: '#8b5cf6',
    },
    {
      label: t('Package renewal rate'),
      value: `${d.renewal_rate ?? 0}%`,
      change: d.renewal_rate_change ?? null,
      icon: faRotate,
      iconColor: '#3b82f6',
    },
    {
      label: t('Average spending per advertiser'),
      value: formatCurrency(d.avg_spending ?? 0),
      change: d.avg_spending_change ?? null,
      icon: faUserTie,
      iconColor: '#f59e0b',
    },
    {
      label: t('Total revenue'),
      value: formatCurrency(d.total_revenue ?? 0),
      change: d.total_revenue_change ?? null,
      icon: faSackDollar,
      iconColor: '#22c55e',
    },
    {
      label: t('Average CTR'),
      value: `${d.avg_ctr ?? 0}%`,
      change: d.avg_ctr_change ?? null,
      icon: faBullseye,
      iconColor: '#ef4444',
    },
  ];

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {t('Advertising Revenue')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted"><span className="text-muted">{t('Home')}</span></li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">{t('Reports & Stats')}</li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">{t('Advertising Revenue')}</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">

            {/* Filter Bar */}
            <div className="card mb-6">
              <div className="card-body py-5">
                <div className="row g-4 align-items-end">
                  <div className="col-12 col-md-4">
                    <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">{t('Country')}</label>
                    <select
                      className="form-select form-select-solid"
                      value={countryId}
                      onChange={(e) => { setCountryId(e.target.value); setCityId(''); }}
                    >
                      <option value="">{t('All countries')}</option>
                      {countries.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">{t('City')}</label>
                    <select
                      className="form-select form-select-solid"
                      value={cityId}
                      onChange={(e) => setCityId(e.target.value)}
                    >
                      <option value="">{t('All cities')}</option>
                      {cities.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">{t('Period')}</label>
                    <select
                      className="form-select form-select-solid"
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                    >
                      {PERIOD_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{t(o.label)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {isLoading ? (
              <PageLoader />
            ) : (
              <>
                {/* Stat Cards */}
                <div className="row g-5 mb-6">
                  {statCards.map((s) => (
                    <StatCard key={s.label} {...s} />
                  ))}
                </div>

                {/* Revenue Charts */}
                <div className="card mb-6">
                  <div className="card-header pt-5 border-bottom-0">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark fs-4 d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faChartPie} style={{ color: '#396A54' }} />
                        {t('Revenue breakdown')}
                      </span>
                      <span className="text-muted mt-1 fw-semibold fs-7">
                        {t('Distribution of advertising spending by package and category')}
                      </span>
                    </h3>
                  </div>
                  <div className="card-body pt-4">
                    <div className="row g-6">

                      {/* Revenue by Package */}
                      <div className="col-12 col-xl-6">
                        <div className="border rounded p-6 h-100">
                          <div className="mb-4">
                            <div className="fw-bold text-dark fs-6">{t('Revenue by package')}</div>
                            <div className="text-muted fs-7">{t('Spending across ad packages')}</div>
                          </div>
                          {revByPackage.length > 0 ? (
                            <div className="row align-items-center g-4">
                              <div className="col-7">
                                <div style={{ height: 220 }}>
                                  <PieChart
                                    labels={revByPackage.map(p => p.label)}
                                    data={revByPackage.map(p => p.total)}
                                    backgroundColor={PACKAGE_COLORS}
                                    options={{
                                      responsive: true,
                                      maintainAspectRatio: false,
                                      cutout: '65%',
                                      plugins: { legend: { display: false } },
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="col-5">
                                <div className="d-flex flex-column gap-3">
                                  {revByPackage.map((p, i) => (
                                    <div key={p.label} className="d-flex align-items-center gap-3">
                                      <span style={{
                                        width: 12, height: 12, borderRadius: '50%',
                                        background: PACKAGE_COLORS[i % PACKAGE_COLORS.length],
                                        flexShrink: 0,
                                      }} />
                                      <div>
                                        <div className="fw-semibold text-dark fs-7">{p.label}</div>
                                        <div className="text-muted fs-8">{formatCurrency(p.total)}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: 180 }}>
                              <span className="text-muted fs-7">{t('No package data available')}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Revenue by Category */}
                      <div className="col-12 col-xl-6">
                        <div className="border rounded p-6 h-100">
                          <div className="mb-4">
                            <div className="fw-bold text-dark fs-6">{t('Revenue by category')}</div>
                            <div className="text-muted fs-7">{t('Spending distribution across ad categories')}</div>
                          </div>
                          {revByCategory.length > 0 ? (
                            <div className="row align-items-center g-4">
                              <div className="col-7">
                                <div style={{ height: 220 }}>
                                  <PieChart
                                    labels={revByCategory.map(c => c.label)}
                                    data={revByCategory.map(c => c.total)}
                                    backgroundColor={CATEGORY_COLORS}
                                    options={{
                                      responsive: true,
                                      maintainAspectRatio: false,
                                      cutout: '65%',
                                      plugins: { legend: { display: false } },
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="col-5">
                                <div className="d-flex flex-column gap-3" style={{ maxHeight: 240, overflowY: 'auto' }}>
                                  {revByCategory.map((c, i) => (
                                    <div key={c.label} className="d-flex align-items-center gap-3">
                                      <span style={{
                                        width: 12, height: 12, borderRadius: '50%',
                                        background: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
                                        flexShrink: 0,
                                      }} />
                                      <div>
                                        <div className="fw-semibold text-dark fs-7">{c.label}</div>
                                        <div className="text-muted fs-8">{formatCurrency(c.total)}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: 180 }}>
                              <span className="text-muted fs-7">{t('No category data available')}</span>
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

                {/* Local vs Foreign Advertisers */}
                <div className="card mb-6">
                  <div className="card-header pt-5 border-bottom-0">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark fs-4 d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faGlobe} style={{ color: '#396A54' }} />
                        {t('Local vs. Foreign Advertisers')}
                      </span>
                      <span className="text-muted mt-1 fw-semibold fs-7">
                        {t('Ratio of advertisements from local versus foreign advertisers per country')}
                      </span>
                    </h3>
                  </div>
                  <div className="card-body pt-2">
                    {localVsForeign.length > 0 ? (
                      <div style={{ height: 320 }}>
                        <BarChart
                          labels={localVsForeign.map(r => r.country)}
                          datasets={[
                            {
                              label: t('Local'),
                              data: localVsForeign.map(r => r.local_pct),
                              backgroundColor: 'rgba(57,106,84,0.85)',
                              borderColor: 'rgba(57,106,84,1)',
                            },
                            {
                              label: t('Foreign'),
                              data: localVsForeign.map(r => r.foreign_pct),
                              backgroundColor: 'rgba(248,168,36,0.85)',
                              borderColor: 'rgba(248,168,36,1)',
                            },
                          ]}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'top',
                                labels: { usePointStyle: true, pointStyle: 'circle', font: { size: 12 } },
                              },
                              tooltip: {
                                callbacks: {
                                  label: (ctx: any) => {
                                    const row = localVsForeign[ctx.dataIndex];
                                    const count = ctx.datasetIndex === 0 ? row.local_count : row.foreign_count;
                                    return ` ${ctx.dataset.label}: ${ctx.raw}% (${count} ${t('ads')})`;
                                  },
                                },
                              },
                            },
                            scales: {
                              x: { stacked: true, grid: { display: false }, ticks: { font: { size: 12 } } },
                              y: {
                                stacked: true,
                                max: 100,
                                ticks: {
                                  font: { size: 11 },
                                  callback: (v: any) => `${v}%`,
                                },
                                grid: { color: 'rgba(0,0,0,0.05)' },
                              },
                            },
                          }}
                        />
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center py-16">
                        <span className="text-muted fs-7">{t('No country data available')}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Top 10 Highest-Spending Advertisers */}
                <div className="card mb-6">
                  <div className="card-header pt-5 border-bottom-0">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark fs-4 d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faTrophy} style={{ color: '#f6c000' }} />
                        {t('Top 10 highest-spending advertisers')}
                      </span>
                      <span className="text-muted mt-1 fw-semibold fs-7">
                        {t('The largest advertisers on the platform, ranked by total amount paid')}
                      </span>
                    </h3>
                  </div>
                  <div className="card-body pt-2">
                    {topSpenders.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4">
                          <thead>
                            <tr className="border-0">
                              <th className="p-0 w-40px text-end text-muted fw-semibold fs-7 text-uppercase">#</th>
                              <th className="p-0 ps-4 text-muted fw-semibold fs-7 text-uppercase">{t('Name')}</th>
                              <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">{t('Country')}</th>
                              <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">{t('Spending')}</th>
                              <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">{t('Package')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topSpenders.map((s, i) => (
                              <tr key={i}>
                                <td className="text-end pe-4">
                                  <span className="text-dark fw-bold fs-6">{i + 1}</span>
                                </td>
                                <td className="ps-4">
                                  <span className="text-dark fw-bold fs-6">{s.name}</span>
                                </td>
                                <td>
                                  <span className="text-muted fw-semibold fs-7">{s.country}</span>
                                </td>
                                <td>
                                  <span className="text-dark fw-bold fs-6">{formatCurrency(s.total_spending)}</span>
                                </td>
                                <td>
                                  <PackageBadge name={s.package} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center py-16">
                        <span className="text-muted fs-7">{t('No advertiser data available')}</span>
                      </div>
                    )}
                  </div>
                </div>

              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAdvertisingRevenueReport;
